import type { LucideIcon } from 'lucide-react'
import {
  BookUser,
  Building2,
  Container,
  FileText,
  Handshake,
  LayoutDashboard,
  ListTodo,
  MessageSquare,
  Settings,
  Wallet,
} from 'lucide-react'

import { can, canAny, CAP, reaches, SURFACE, type Surface } from '@/lib/can'
import type { Capability, CurrentUser, Role, SeededRole } from '@/types/domain'

/* Navigation is filtered by what the session may do and what its role reaches,
 * so nobody is shown a section they cannot enter. A section whose surface the
 * role holds at «Нет доступа» (#129) is absent, exactly as one whose capability
 * it lacks. PREFER ABSENCE TO DISABLED
 * CONTROLS. A Sales user does not see a greyed-out Finance section; the section
 * is not in their navigation. Disabled controls invite the question "why not",
 * and at this team size the answer is a WhatsApp message to a founder.
 *
 * This is a UX affordance ONLY. Server-side authorisation is the sole real
 * enforcement (AD-21) — the client never treats hiding as security, and the API
 * returns no data the user may not see regardless of what is rendered here. */

/* Labels are i18n KEYS, not text. Navigation is module-level data evaluated
 * once at import, so a translated string baked in here would be frozen in
 * whichever language happened to be active at startup and would not follow a
 * language switch. The component translates at render instead. */

/** The `nav` catalogue keys usable as a navigation label. Typed as literals so
 *  `t(item.labelKey)` satisfies i18next's strict key checking. */
export type NavLabelKey =
  | 'nav.dashboard'
  | 'nav.myTasks'
  | 'nav.quotes'
  | 'nav.cases'
  | 'nav.chat'
  | 'nav.finance'
  | 'nav.clients'
  | 'nav.counterparties'
  | 'nav.directories'
  | 'nav.settings'

export type NavSectionKey = 'nav.sections.main' | 'nav.sections.company'

/** The keys of the sidebar's count badges. `finance` is the payment-confirmation
 *  queue (QW-10) — the same all-time queue «Мои задачи»'s `payments-to-confirm`
 *  section counts (S-2b.2); `quotes` and `cases` are plumbing nobody passes
 *  yet — see AppShell. */
export type NavCountKey = 'quotes' | 'cases' | 'chat' | 'finance'

export interface NavItem {
  to: string
  /** Key into the `nav` catalogue, e.g. `nav.cases`. */
  labelKey: NavLabelKey
  icon: LucideIcon
  /** The capability the item's screen is read with. Omitted means none is
   *  needed. Tested with `can()`, so the superuser passes. */
  capability?: Capability
  /** For a screen whose parts are opened by different capabilities: the item
   *  shows when the reader holds ANY of them (`canAny`). Settings is the one
   *  user — each of its tabs has its own guard (S-7). Never a role name: a
   *  custom role matches no arm of a list written at build time (0021). */
  anyCapability?: readonly Capability[]
  /** The scopable surface the item's list is read from. The item shows only
   *  when the session reaches it (`reaches()`, so the superuser passes): a role
   *  at «Нет доступа» on quotes has no Quotes stop, rather than a stop that
   *  opens an empty list (#129). Never a capability in its place — holding
   *  `quote.create` does not make the quote list readable. */
  surface?: Surface
  /** Key into the counts map, when the item shows a count badge. */
  countKey?: NavCountKey
}

export interface NavSection {
  /** Key into the `nav.sections` catalogue. */
  labelKey: NavSectionKey
  items: NavItem[]
}

const SECTIONS: NavSection[] = [
  {
    labelKey: 'nav.sections.main',
    items: [
      {
        to: '/',
        labelKey: 'nav.dashboard',
        icon: LayoutDashboard,
        /* `GET /api/dashboard` is guarded by this capability server-side, so
           the stop follows it (S-2b.1) — the same test `homePathFor` makes.
           Without it "/" sends the reader to their tasks, and a Dashboard
           stop would be a link that lands somewhere else. */
        capability: CAP.financeReceivablesRead,
      },
      /* Every reader's: the server serves each of them only the sections they
         hold, and a reader holding none is told so on the page. */
      { to: '/tasks', labelKey: 'nav.myTasks', icon: ListTodo },
      {
        to: '/quotes',
        labelKey: 'nav.quotes',
        icon: FileText,
        surface: SURFACE.quotes,
        countKey: 'quotes',
      },
      {
        to: '/cases',
        labelKey: 'nav.cases',
        icon: Container,
        surface: SURFACE.cases,
        countKey: 'cases',
      },
      // Chat spans quotes AND cases, so it sits beside them rather than under
      // either. Its badge is the reader's total unread across both tabs — the
      // one number that answers "is anybody waiting on me".
      { to: '/chat', labelKey: 'nav.chat', icon: MessageSquare, countKey: 'chat' },
      {
        to: '/finance',
        labelKey: 'nav.finance',
        icon: Wallet,
        /* The capability every read on the page is guarded by server-side —
           the balances, aging, payments, approvals and FX tabs alike. Not the
           role name: a custom role granted it would otherwise never see the
           section it can open. Not "receivables OR confirm" either: confirm
           alone opens no tab, and the item would land its holder on a 403.
           The dashboard's money tiles follow the same capability. */
        capability: CAP.financeReceivablesRead,
        // The payments awaiting this reader's confirmation (QW-10).
        countKey: 'finance',
      },
    ],
  },
  {
    labelKey: 'nav.sections.company',
    items: [
      /* Clients is the directory tab people actually come for — every quote
       * starts by picking one — so it is a section of its own with its own
       * path, and a client has a page under it (`/clients/:clientId`). */
      { to: '/clients', labelKey: 'nav.clients', icon: Building2, surface: SURFACE.clients },
      /* Everyone who provides us a service (S-22b), after the clients who pay
       * for it. Gated on the read the whole section is served by: without it
       * the list is empty and a record is not found, so the stop would open a
       * page with nothing on it. Sales holds none of the counterparty keys. */
      {
        to: '/counterparties',
        labelKey: 'nav.counterparties',
        icon: Handshake,
        capability: CAP.counterpartyRead,
      },
      { to: '/directories', labelKey: 'nav.directories', icon: BookUser },
      {
        to: '/settings',
        labelKey: 'nav.settings',
        icon: Settings,
        /* The capabilities that guard a Settings tab
           (`features/settings/settingsTabs.ts`; their test holds the two lists
           together) — `route.template.manage` among them since S-10, which
           opens «Маршруты» to Operations and Pricing. Not the `.read` ones:
           they guard no route, and the stop would open a page with no tab. */
        anyCapability: [
          CAP.adminUsersManage,
          CAP.adminSettingsManage,
          CAP.adminRolesManage,
          CAP.routeTemplateManage,
        ],
      },
    ],
  },
]

/** The sections this session is shown: an item needs its capability (when it
 *  names one) AND one of its any-of capabilities (when it names some) AND to
 *  reach its surface (when it names one). */
export function navigationFor(user: CurrentUser): NavSection[] {
  return SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter(
      (item) =>
        (!item.capability || can(user, item.capability)) &&
        (!item.anyCapability || canAny(user, ...item.anyCapability)) &&
        (!item.surface || reaches(user, item.surface)),
    ),
  })).filter((section) => section.items.length > 0)
}

/** Whether a nav item is the one the current location is on.
 *
 *  `NavLink`'s own `isActive` matches on PATHNAME only, which cannot separate
 *  two items that share a pathname and differ by a search parameter: both
 *  would light up, and the sidebar's sliding pill — one element with a shared
 *  layoutId — cannot be in two places, so it would flicker between them.
 *
 *  The rule is narrow on purpose. An item carrying a search param is active
 *  only when the location's parameters agree with the ones it names (extra
 *  parameters the item does not mention are ignored, so a `?client=` beside
 *  the one it claims does not deactivate it). An item carrying none is active
 *  on its path only when no SIBLING item claims the current parameters.
 *
 *  `siblings` is the other items sharing this item's pathname; the caller has
 *  the section list in hand and this stays a pure function of it. */
export function isNavItemActive(
  item: Pick<NavItem, 'to'>,
  location: { pathname: string; search: string },
  siblings: readonly Pick<NavItem, 'to'>[] = [],
): boolean {
  const [path, query = ''] = item.to.split('?')
  // The dashboard is the only prefix that would swallow every route below it;
  // every other item owns its path AND the records under it — a case's page
  // is still the Cases section, a client's still Clients.
  const pathMatches =
    path === '/'
      ? location.pathname === '/'
      : location.pathname === path || location.pathname.startsWith(path + '/')
  if (!pathMatches) return false

  const current = new URLSearchParams(location.search)
  const claims = (spec: string) =>
    [...new URLSearchParams(spec)].every(([k, v]) => current.get(k) === v)

  if (query) return claims(query)

  return !siblings.some((other) => {
    const [otherPath, otherQuery = ''] = other.to.split('?')
    return otherPath === path && otherQuery !== '' && claims(otherQuery)
  })
}

/* The home surface, by capability (S-2b.1).
 *
 * "/" is not the dashboard for everyone. The dashboard answers the founders'
 * morning question — live figures across every case — and its read
 * (`GET /api/dashboard`) is guarded by `finance.receivables.read`. A reader
 * holding it lands there; everyone else lands on «Мои задачи», the quotes and
 * cases waiting on THEM, which the server composes per reader.
 *
 * The capability, never the role's name (B4). Roles are runtime records
 * (migration 0021): the role-name switch this replaced sent a custom role
 * holding the read to `/cases`, and would send a Finance-named role that lost
 * it onto a dashboard whose read the server refuses. `can()` passes the
 * superuser.
 *
 * No home path carries a filter. Pricing and Operations used to land on
 * `?assignee=me`, which no list ever read (QW-9, C-7): the URL promised "mine"
 * over a list whose rows the SERVER scopes (AD-5). «Мои задачи» is that promise
 * kept, by the server. */
export function homePathFor(user: CurrentUser): string {
  return can(user, CAP.financeReceivablesRead) ? '/' : '/tasks'
}

/** Whether this reader's home surface is the dashboard itself.
 *
 * Exists so the index route can render the dashboard in place rather than
 * redirecting to it — a redirect from "/" to "/" is a loop. */
export function homeIsDashboard(user: CurrentUser): boolean {
  return homePathFor(user) === '/'
}

/** The `roles` catalogue key for one of the five SEEDED roles.
 *
 *  Roles are runtime-definable since migration 0021, so a role key is no longer
 *  guaranteed to have a translation — a founder can create `ops-lead` and the
 *  catalogue knows nothing about it. This narrows to the seeded five, and
 *  `roleLabel` below is what call sites should use.
 *
 *  Translate at the point of render: `t(roleLabelKey('sales'))`. */
export function roleLabelKey(role: SeededRole): `roles.${SeededRole}` {
  return `roles.${role}`
}

/** The five keys the catalogue has translations for. */
const SEEDED_ROLES: readonly SeededRole[] = [
  'sales',
  'pricing',
  'operations',
  'finance',
  'super-admin',
]

/** The description each seeded role was given by migration 0021, verbatim.
 *
 *  Kept HERE and not in `en.ts`, because it is not copy: it is the server's
 *  stored text, and its only job is to recognise a description nobody has
 *  edited (`roleDescription` below). The English catalogue happens to say the
 *  same words today; if a writer rewords `en.roleDescriptions`, this must not
 *  move with it. `navigation.test.ts` reads the migration and fails when the
 *  two part. */
export const SEEDED_ROLE_DESCRIPTIONS: Readonly<Record<SeededRole, string>> = {
  sales: 'Owns the client relationship and the quote pipeline.',
  pricing: 'Builds the cost side of quotes and maintains agents.',
  operations: 'Runs shipments once a quote converts to a case.',
  finance: 'Invoices, payments, receivables and the cross-case views.',
  'super-admin': 'Full access, user management, and the roles themselves.',
}

/** Whether a role key is one of the five the product ships with. */
export function isSeededRole(role: Role): role is SeededRole {
  return (SEEDED_ROLES as readonly string[]).includes(role)
}

/** How to render a role's description.
 *
 *  A description is FREE TEXT an admin can edit, unlike a name, which is
 *  translated by the role's key. So a seeded role's description is translated
 *  only while it is still exactly what 0021 seeded; the moment an admin types
 *  their own words, those words are the description, in every language. An
 *  exact comparison rather than a trimmed or case-folded one: a "near" seed is
 *  an edit, and rendering a translation over somebody's edit is the one wrong
 *  answer here. Same discriminated shape as `roleLabel`, for the same reason. */
export function roleDescription(
  role: Role,
  stored: string,
):
  | { translated: true; key: `roleDescriptions.${SeededRole}` }
  | { translated: false; text: string } {
  if (isSeededRole(role) && stored === SEEDED_ROLE_DESCRIPTIONS[role]) {
    return { translated: true, key: `roleDescriptions.${role}` }
  }
  return { translated: false, text: stored }
}

/** How to render a role's name.
 *
 *  The five shipped roles are TRANSLATED — "Pricing" reads as «Ценообразование»
 *  in Russian, and a founder switching languages should not see an English word
 *  in a Russian sentence. A role somebody created at runtime has no translation
 *  and never will, so it falls back to the display name they typed. That is
 *  precisely why the server stores a name alongside the key.
 *
 *  Returns a discriminated result rather than calling `t` itself, because this
 *  module is data and must not depend on an i18n instance — the caller has a
 *  `t` in hand and this tells it which of the two things to do. */
export function roleLabel(
  role: Role,
  name?: string,
): { translated: true; key: `roles.${SeededRole}` } | { translated: false; text: string } {
  if (isSeededRole(role)) {
    return { translated: true, key: roleLabelKey(role) }
  }
  // The typed name when we have one; otherwise the raw key, which is at least
  // something a founder recognises from the role editor.
  return { translated: false, text: name?.trim() || role }
}
